import { formatBytes } from '@ukinfo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { autoSyncEnabled, offlineUsage, removeAllOffline, setAutoSyncEnabled, useOfflineAvailable } from '../lib/offline';
import { Button, Card, CardBody, Checkbox, SectionTitle, useToast } from './ui';

/**
 * Einstellungen "Offline am Platz" - gelten für dieses Gerät: automatisch vorhalten ein/aus, was gespeichert
 * ist, alles entfernen.
 */
export function OfflineCard(): JSX.Element | null {
  const { userId, available } = useOfflineAvailable();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [auto, setAuto] = useState(autoSyncEnabled);
  const usage = useQuery({
    queryKey: ['offline-entries', 'usage', userId],
    queryFn: () => offlineUsage(userId ?? ''),
    enabled: available,
  });
  if (!available || !userId) return null;

  const total = (usage.data?.textBytes ?? 0) + (usage.data?.videoBytes ?? 0);
  return (
    <Card>
      <CardBody className="space-y-3">
        <SectionTitle title="Offline am Platz" subtitle="Einheiten ohne Netz ansehen – gilt für dieses Gerät" />
        <Checkbox
          checked={auto}
          onChange={(value) => { setAuto(value); setAutoSyncEnabled(value); }}
          label="Trainings der nächsten 48 Stunden automatisch offline vorhalten"
        />
        <p className="text-xs text-slate-500">
          Beim Öffnen der App werden – wenn du online bist – die Einheiten der kommenden Trainings deiner Mannschaften im Hintergrund gespeichert
          (ohne Videos). Beim Abmelden wird alles von diesem Gerät gelöscht.
        </p>
        <p className="text-sm text-slate-700">
          {usage.data ? `${usage.data.entries} ${usage.data.entries === 1 ? 'Einheit' : 'Einheiten'} gespeichert · ${formatBytes(total)}` : 'Wird geprüft …'}
        </p>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/offline"
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:border-slate-400 hover:bg-slate-50 sm:min-h-0"
          >
            Offline gespeichert öffnen
          </Link>
          {(usage.data?.entries ?? 0) > 0 && (
            <Button
              variant="ghost"
              className="text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              onClick={() => {
                if (!window.confirm('Alle offline gespeicherten Einheiten von diesem Gerät entfernen?')) return;
                void removeAllOffline(userId)
                  .then(() => queryClient.invalidateQueries({ queryKey: ['offline-entries'] }))
                  .then(() => toast.show('Alles vom Gerät entfernt'))
                  .catch(() => toast.show('Entfernen fehlgeschlagen', 'error'));
              }}
            >
              Alles entfernen
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
